import {
  Button,
  Checkbox,
  Input,
  Label,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  cn
} from '@repo/ui'
import {
  ArrowLeft,
  ArrowUp,
  Check,
  ChevronRight,
  FilePlus2,
  LoaderCircle,
  Plus,
  RotateCw,
  Tag,
  Upload,
  X
} from 'lucide-react'
import { useEffect, useEffectEvent, useImperativeHandle, useMemo, useRef, useState } from 'react'
import type { ConversationCommand } from '@/api/conversation'
import type { Ref } from 'react'

export const maxConversationTags = 32
export const maxConversationAttachments = 32
export const maxConversationSuggestions = 5

// Narrowing starts once the typed text is long enough to mean something, as the design
// authority does: docs/design/snowvault-chat-shell.html:1755-1761.
const minSuggestionQueryLength = 2
const suggestionListID = 'conversation-composer-suggestions'
const suggestionOptionID = (index: number) => `${suggestionListID}-option-${index}`
const noSuggestions: ReadonlyArray<string> = []
const conversationUploadExtensions = ['.docx', '.pdf', '.xlsx', '.pptx', '.txt', '.eml', '.csv'] as const
const conversationUploadAccept = conversationUploadExtensions.join(',')
const maxConversationUploadBytes = 25 * 1024 * 1024

export type ConversationComposerOption = { id: number; label: string; secondary?: string; updatedAtTsMs?: number }
export type ConversationComposerPage = { items: Array<ConversationComposerOption>; hasMore: boolean }
export type ConversationComposerLoadRequest = { page: number; search: string }
export type ConversationComposerUploadResult = { document: ConversationComposerOption; reused: boolean }
export type ConversationComposerContextActions = { openDocuments: () => void; uploadFile: () => void }
export type ConversationComposerLoader = (
  request?: ConversationComposerLoadRequest
) => Promise<Array<ConversationComposerOption> | ConversationComposerPage>

const openings = [
  { label: 'Answer a question from the workspace', seed: 'Answer ' },
  { label: 'Draft a response or section', seed: 'Draft ' },
  { label: 'Check figures against their source', seed: 'Check ' },
  { label: 'Turn approved work into something new', seed: 'Turn ' }
] as const

type ConversationUploadFeedback =
  | { state: 'uploading'; file: File }
  | { state: 'success'; message: string }
  | { state: 'error'; file: File; message: string; retryable: boolean }

function conversationUploadValidation(file: File): string | undefined {
  const name = file.name.toLowerCase()
  if (!conversationUploadExtensions.some((extension) => name.endsWith(extension))) {
    return `${file.name} is not supported. Upload a PDF, Word, Excel, PowerPoint, text, email, or CSV file.`
  }
  if (file.size > maxConversationUploadBytes) {
    return `${file.name} is larger than the 25 MB limit.`
  }
  return undefined
}

function conversationUploadIsRetryable(cause: unknown): boolean {
  return !(cause instanceof Error && 'retryable' in cause && cause.retryable === false)
}

function dataTransferHasFiles(dataTransfer: DataTransfer): boolean {
  return dataTransfer.files.length > 0 || Array.from(dataTransfer.types).includes('Files')
}

function OptionPicker({
  label,
  action,
  icon,
  load,
  selected,
  maximum,
  onAdd,
  onCreate
}: {
  label: string
  action: string
  icon: 'tag' | 'document'
  load: ConversationComposerLoader
  selected: Array<number>
  maximum: number
  onAdd: (option: ConversationComposerOption) => void
  onCreate?: (name: string) => Promise<ConversationComposerOption>
}) {
  const [loading, setLoading] = useState(true)
  const [options, setOptions] = useState<Array<ConversationComposerOption>>([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string>()
  const [createError, setCreateError] = useState<string>()
  const [creating, setCreating] = useState(false)
  const [request, setRequest] = useState({ page: 1, search: '' })
  const searchInput = useRef<HTMLInputElement>(null)
  const mounted = useRef(true)
  const creatingTag = useRef(false)
  const Icon = icon === 'tag' ? Tag : FilePlus2
  const query = search.trim()
  const tooShort = query.length > 0 && query.length < 3
  const atLimit = selected.length >= maximum
  const canCreate =
    onCreate !== undefined &&
    query !== '' &&
    !loading &&
    !error &&
    !options.some((option) => option.label.trim().toLowerCase() === query.toLowerCase())

  useEffect(() => {
    if (!creating) searchInput.current?.focus()
  }, [creating])
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const requestPage = (nextRequest: typeof request) => {
    setLoading(true)
    setError(undefined)
    if (nextRequest.page === 1) {
      setOptions([])
      setHasMore(false)
    }
    setRequest(nextRequest)
  }
  // Request state owns fetching. Parent chat updates may supply an equivalent
  // loader callback without requesting another copy of the current page.
  const loadPage = useEffectEvent(load)
  useEffect(() => {
    let current = true
    const fetchPage = () => {
      void loadPage(request)
        .then((result) => {
          if (!current) return
          const next = Array.isArray(result) ? { items: result, hasMore: false } : result
          setOptions((previous) => (request.page === 1 ? next.items : [...previous, ...next.items]))
          setPage(request.page)
          setHasMore(next.hasMore)
        })
        .catch((loadError: unknown) => {
          if (!current) return
          setError(loadError instanceof Error ? loadError.message : `Could not load ${label.toLowerCase()}`)
        })
        .finally(() => {
          if (current) setLoading(false)
        })
    }
    // Change request ownership before debouncing so late responses cannot
    // repopulate rows belonging to the previous search.
    const timer = request.search !== '' && request.page === 1 ? setTimeout(fetchPage, 300) : undefined
    if (timer === undefined) fetchPage()
    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [request, label])

  const createOption = async () => {
    if (!onCreate || !canCreate || atLimit || creatingTag.current) return
    creatingTag.current = true
    setCreating(true)
    setCreateError(undefined)
    try {
      const option = await onCreate(query)
      if (!mounted.current) return
      setOptions((previous) => [...previous.filter((item) => item.id !== option.id), option])
      onAdd(option)
    } catch (cause) {
      if (mounted.current)
        setCreateError(cause instanceof Error ? cause.message : 'Could not create this tag. Try again.')
    } finally {
      creatingTag.current = false
      if (mounted.current) setCreating(false)
    }
  }

  return (
    <div>
      <div className="px-2 pb-2">
        <Input
          ref={searchInput}
          aria-label={`Search ${label.toLowerCase()}`}
          placeholder={icon === 'tag' ? 'Search tags' : 'Search workspace documents'}
          value={search}
          disabled={creating}
          onChange={(event) => {
            const value = event.currentTarget.value
            setSearch(value)
            setCreateError(undefined)
            const nextSearch = value.trim().length >= 3 ? value.trim() : ''
            if (nextSearch !== request.search) requestPage({ page: 1, search: nextSearch })
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
            event.preventDefault()
          }}
        />
      </div>
      {atLimit && (
        <p className="px-3 pb-2 text-xs text-muted-foreground" role="status">
          You can select up to {maximum} {icon === 'tag' ? 'tags' : 'documents'}. Remove one to add another.
        </p>
      )}
      {tooShort ? (
        <p className="px-3 py-2 text-sm text-muted-foreground" role="status">
          Type at least 3 characters to search.
        </p>
      ) : (
        loading && (
          <p className="px-3 py-2 text-sm text-muted-foreground" role="status">
            {query ? 'Searching…' : 'Loading…'}
          </p>
        )
      )}
      {error && (
        <div className="flex items-center justify-between gap-2 px-3 py-2" role="alert">
          <p className="text-sm text-destructive">{error}</p>
          <Button type="button" size="sm" variant="ghost" onClick={() => requestPage({ ...request })}>
            Retry
          </Button>
        </div>
      )}
      {!tooShort && !loading && !error && options.length === 0 && (
        <p className="px-3 py-2 text-sm text-muted-foreground">
          {query
            ? `No matching ${icon === 'tag' ? 'tags' : 'documents'}.`
            : icon === 'tag'
              ? onCreate
                ? 'No tags yet. Type a name to create one.'
                : 'No tags available.'
              : 'No documents available.'}
        </p>
      )}
      {!tooShort &&
        options.map((option) => (
          <button
            key={option.id}
            type="button"
            disabled={selected.includes(option.id) || atLimit || creating}
            aria-label={`${action} ${option.label}`}
            className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-60"
            onClick={() => onAdd(option)}
          >
            <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="min-w-0 flex-1 break-words">
              <span className="block">{option.label}</span>
              {option.secondary && <span className="block text-xs text-muted-foreground">{option.secondary}</span>}
            </span>
            {selected.includes(option.id) && <Check className="size-4 shrink-0 text-primary" aria-label="Selected" />}
          </button>
        ))}
      {createError && (
        <p role="alert" className="px-3 py-2 text-sm text-destructive">
          {createError}
        </p>
      )}
      {canCreate && (
        <button
          type="button"
          aria-label={`Create tag ${query}`}
          disabled={atLimit || creating}
          className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-60"
          onClick={() => {
            void createOption()
          }}
        >
          {creating ? (
            <LoaderCircle className="size-4 shrink-0 animate-spin" aria-hidden="true" />
          ) : (
            <Plus className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <span className="min-w-0 flex-1 break-words">
            <span className="block">{creating ? 'Creating tag…' : `Create “${query}”`}</span>
            <span className="block text-xs text-muted-foreground">
              Create an organisation tag and add it to this message.
            </span>
          </span>
        </button>
      )}
      {!tooShort && hasMore && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full"
          disabled={loading || creating}
          onClick={() => requestPage({ page: page + 1, search: request.search })}
        >
          {icon === 'tag' ? 'Load more tags' : 'Load more documents'}
        </Button>
      )}
    </div>
  )
}

export function ConversationComposer({
  projectName,
  globalContentEnabled,
  attachmentsEnabled,
  onSubmit,
  loadTags,
  loadDocuments,
  createTag,
  uploadDocument,
  showOpenings = true,
  suggestions = noSuggestions,
  seed,
  pendingQuestionKey,
  contextActions,
  onDocumentsChange,
  idFactory = () => crypto.randomUUID()
}: {
  projectName: string
  globalContentEnabled: boolean
  attachmentsEnabled: boolean
  onSubmit: (command: ConversationCommand) => Promise<void>
  loadTags: ConversationComposerLoader
  loadDocuments: ConversationComposerLoader
  createTag?: (name: string) => Promise<ConversationComposerOption>
  uploadDocument?: (file: File) => Promise<ConversationComposerUploadResult>
  showOpenings?: boolean
  suggestions?: ReadonlyArray<string>
  // seed is an opening handed to the composer from elsewhere, currently the brief
  // panel's answer arrow. The nonce is what makes a repeat of the same text a new
  // request, so asking the same open slot twice seeds twice.
  seed?: { text: string; nonce: number }
  pendingQuestionKey?: string
  contextActions?: Ref<ConversationComposerContextActions>
  onDocumentsChange?: (documents: Array<ConversationComposerOption>) => void
  idFactory?: () => string
}) {
  const [content, setContent] = useState('')
  const [includeOrgContent, setIncludeOrgContent] = useState(false)
  const [tags, setTags] = useState<Array<ConversationComposerOption>>([])
  const [documents, setDocuments] = useState<Array<ConversationComposerOption>>([])
  const [contextOpen, setContextOpen] = useState(false)
  const [contextView, setContextView] = useState<'home' | 'documents' | 'tags'>('home')
  const contextHomeAction = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (contextOpen && contextView === 'home') contextHomeAction.current?.focus()
  }, [contextOpen, contextView])
  useEffect(() => onDocumentsChange?.(documents), [documents, onDocumentsChange])
  useEffect(() => () => onDocumentsChange?.([]), [onDocumentsChange])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string>()
  const [uploadFeedback, setUploadFeedback] = useState<ConversationUploadFeedback>()
  const [draggingFile, setDraggingFile] = useState(false)
  const [highlightedSuggestion, setHighlightedSuggestion] = useState(-1)
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false)
  const [suggestionFills, setSuggestionFills] = useState(0)
  const [revealedQuestionKey, setRevealedQuestionKey] = useState<string>()
  const [showAllOpenings, setShowAllOpenings] = useState(false)
  // Initialised from the seed that is already there, so a remount consumes nothing.
  // The shell holds the seed for the lifetime of the chat and the composer unmounts on
  // reconnect, on a read-only chat and on switching chats; without this, any of those
  // would replay the last stem, sometimes one pressed in a different conversation.
  const [appliedSeedNonce, setAppliedSeedNonce] = useState<number | undefined>(seed?.nonce)
  const retryCommand = useRef<{ command: ConversationCommand; fingerprint: string } | undefined>(undefined)
  const fileDragDepth = useRef(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploading = uploadFeedback?.state === 'uploading'
  const canSubmit = content.trim() !== '' && !submitting && !uploading
  const selectedTagIDs = useMemo(() => tags.map((tag) => tag.id), [tags])
  const selectedDocumentIDs = useMemo(() => documents.map((document) => document.id), [documents])
  const uploadsEnabled = attachmentsEnabled && uploadDocument !== undefined
  useImperativeHandle(
    contextActions,
    () => ({
      openDocuments: () => {
        if (!attachmentsEnabled) return
        setRevealedQuestionKey(pendingQuestionKey)
        setContextView('documents')
        setContextOpen(true)
      },
      uploadFile: () => {
        if (!uploadsEnabled || submitting || uploading || documents.length >= maxConversationAttachments) return
        setRevealedQuestionKey(pendingQuestionKey)
        setContextOpen(false)
        fileInputRef.current?.click()
      }
    }),
    [attachmentsEnabled, uploadsEnabled, submitting, uploading, documents.length, pendingQuestionKey]
  )
  const fingerprint = JSON.stringify({
    content: content.trim(),
    includeOrgContent,
    tagIds: selectedTagIDs,
    attachedDocumentIds: selectedDocumentIDs
  })
  const invalidateAmbiguousCommand = () => {
    retryCommand.current = undefined
    setError(undefined)
  }

  const resetFileDrag = () => {
    fileDragDepth.current = 0
    setDraggingFile(false)
  }

  const uploadFile = async (file: File) => {
    if (!attachmentsEnabled || uploadDocument === undefined || uploading) return
    if (selectedDocumentIDs.length >= maxConversationAttachments) {
      setUploadFeedback({
        state: 'error',
        file,
        message: `Remove a source before attaching ${file.name}.`,
        retryable: false
      })
      return
    }
    const validationError = conversationUploadValidation(file)
    if (validationError !== undefined) {
      setUploadFeedback({ state: 'error', file, message: validationError, retryable: false })
      return
    }

    invalidateAmbiguousCommand()
    setUploadFeedback({ state: 'uploading', file })
    try {
      const result = await uploadDocument(file)
      setDocuments((current) =>
        current.some((document) => document.id === result.document.id) ? current : [...current, result.document]
      )
      setUploadFeedback({
        state: 'success',
        message: result.reused
          ? `${result.document.label} was already in Content and is attached.`
          : `${result.document.label} was uploaded to Content and attached.`
      })
    } catch (cause) {
      setUploadFeedback({
        state: 'error',
        file,
        message: cause instanceof Error && cause.message !== '' ? cause.message : `${file.name} could not be uploaded.`,
        retryable: conversationUploadIsRetryable(cause)
      })
    }
  }

  // Matching is a case-insensitive substring test over the whole phrase, verb and object
  // together, so typing an object narrows as well as typing a verb.
  const matchedSuggestions = useMemo(() => {
    const query = content.trim().toLowerCase()
    if (query.length < minSuggestionQueryLength) return []
    return suggestions.filter((phrase) => phrase.toLowerCase().includes(query)).slice(0, maxConversationSuggestions)
  }, [content, suggestions])
  // Without a corpus the composer stays a plain textarea: there is no popup to own, so
  // announcing a combobox would promise narrowing that cannot happen.
  const narrowingEnabled = suggestions.length > 0
  const suggestionsOpen = narrowingEnabled && !suggestionsDismissed && matchedSuggestions.length > 0
  // A stored highlight can outlive the list it was chosen from, because the match list also
  // changes when the corpus itself is replaced. Read the highlight against the current matches
  // so it can never name an entry that is not on screen, or step from an index that is not.
  const activeSuggestion = highlightedSuggestion < matchedSuggestions.length ? highlightedSuggestion : -1
  const highlightedSuggestionID =
    suggestionsOpen && activeSuggestion > -1 ? suggestionOptionID(activeSuggestion) : undefined

  const resetNarrowing = () => {
    setHighlightedSuggestion(-1)
    setSuggestionsDismissed(false)
  }

  const fillFromSuggestion = (phrase: string) => {
    invalidateAmbiguousCommand()
    setContent(phrase)
    setSuggestionsDismissed(true)
    setHighlightedSuggestion(-1)
    setSuggestionFills((current) => current + 1)
  }

  // Selecting a phrase leaves the caret where the user would keep typing, and returns focus
  // to the composer after a click moved it to the entry. Filling the composer from
  // anywhere also retires the last failed command, because a retry would now send text
  // the user can no longer see. The ref is cleared here rather than at the two call
  // sites so the seed path, which runs during render, does not have to touch it.
  useEffect(() => {
    if (suggestionFills === 0) return
    retryCommand.current = undefined
    const editor = textareaRef.current
    if (!editor) return
    editor.focus()
    editor.setSelectionRange(editor.value.length, editor.value.length)
  }, [suggestionFills])

  // A seed takes the same path a task opening takes: set the content, narrow on it
  // afresh, then let the suggestion-fill effect above focus with the caret at the end,
  // so the user carries on typing the sentence rather than repositioning first.
  //
  // Adjusted during render rather than in an effect. React sanctions this for state
  // derived from a prop change, and it avoids the cascading re-render an effect would
  // cost. The applied nonce is what makes it fire once per request, and what lets the
  // same stem seed again.
  if (seed !== undefined && seed.nonce !== appliedSeedNonce) {
    setAppliedSeedNonce(seed.nonce)
    setError(undefined)
    setContent(seed.text)
    if (pendingQuestionKey !== undefined) setRevealedQuestionKey(pendingQuestionKey)
    resetNarrowing()
    setSuggestionFills((current) => current + 1)
  }

  const send = async (command?: ConversationCommand) => {
    const next =
      command ??
      (retryCommand.current?.fingerprint === fingerprint ? retryCommand.current.command : undefined) ??
      ({
        type: 'task',
        clientTurnId: idFactory(),
        content: content.trim(),
        includeOrgContent,
        tagIds: selectedTagIDs,
        attachedDocumentIds: selectedDocumentIDs
      } satisfies ConversationCommand)
    retryCommand.current = { command: next, fingerprint }
    setSubmitting(true)
    setError(undefined)
    try {
      await onSubmit(next)
      retryCommand.current = undefined
      setContent('')
      setTags([])
      setDocuments([])
      setUploadFeedback(undefined)
      // The sent turn takes the narrowing state with it: the next composition starts at rest.
      resetNarrowing()
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'The message was not sent.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-[44rem] px-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6">
      {uploadsEnabled && (
        <input
          ref={fileInputRef}
          type="file"
          accept={conversationUploadAccept}
          aria-label="Choose a file to upload"
          tabIndex={-1}
          className="sr-only"
          disabled={submitting || uploading}
          onChange={(event) => {
            const file = event.currentTarget.files?.[0]
            event.currentTarget.value = ''
            if (file !== undefined) void uploadFile(file)
          }}
        />
      )}
      {pendingQuestionKey !== undefined && revealedQuestionKey !== pendingQuestionKey ? (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            className="min-h-11 rounded-full px-4"
            onClick={() => setRevealedQuestionKey(pendingQuestionKey)}
          >
            Start another request
          </Button>
        </div>
      ) : (
        <>
          {showOpenings && content === '' && (
            <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2" aria-label="Task openings">
              {(showAllOpenings ? openings : openings.slice(0, 3)).map((opening) => (
                <button
                  key={opening.seed}
                  type="button"
                  className="min-h-12 rounded-lg border px-3 py-2 text-left text-sm hover:border-primary/30 hover:bg-accent"
                  onClick={() => {
                    invalidateAmbiguousCommand()
                    setContent(opening.seed)
                    // Seeding a verb is a fresh start, so it narrows on the seed even if the last
                    // composition dismissed the list or left a highlight behind.
                    resetNarrowing()
                    requestAnimationFrame(() => {
                      textareaRef.current?.focus()
                      textareaRef.current?.setSelectionRange(opening.seed.length, opening.seed.length)
                    })
                  }}
                >
                  {opening.label}
                </button>
              ))}
              {!showAllOpenings && (
                <button
                  type="button"
                  className="min-h-11 rounded-lg px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
                  onClick={() => setShowAllOpenings(true)}
                >
                  More examples
                </button>
              )}
            </div>
          )}
          {suggestionsOpen && (
            <div
              id={suggestionListID}
              role="listbox"
              aria-label="Task suggestions"
              className="mb-2 overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-sm"
            >
              {matchedSuggestions.map((phrase, index) => {
                const [verb, ...rest] = phrase.split(' ')
                const object = rest.join(' ')
                return (
                  <button
                    key={suggestionOptionID(index)}
                    id={suggestionOptionID(index)}
                    type="button"
                    role="option"
                    aria-selected={index === activeSuggestion}
                    // Focus stays on the combobox and travels by aria-activedescendant, so the
                    // entries are reachable by the arrow keys and never by Tab.
                    tabIndex={-1}
                    className={cn(
                      'block w-full px-3 py-2 text-left text-sm hover:bg-accent',
                      index === activeSuggestion && 'bg-accent'
                    )}
                    onClick={() => fillFromSuggestion(phrase)}
                  >
                    <span className="font-medium">{verb}</span>
                    {object === '' ? '' : ` ${object}`}
                  </button>
                )
              })}
            </div>
          )}
          <Popover
            open={contextOpen}
            onOpenChange={(open) => {
              setContextOpen(open)
              if (!open) setContextView('home')
            }}
          >
            <PopoverAnchor asChild>
              <form
                aria-label="Message composer"
                data-dragging-file={draggingFile || undefined}
                className={cn(
                  'relative rounded-2xl border bg-background shadow-sm transition-[border-color,box-shadow] focus-within:border-primary/30 focus-within:shadow-md',
                  draggingFile && 'border-primary shadow-md ring-2 ring-primary/20'
                )}
                onDragEnter={(event) => {
                  if (!dataTransferHasFiles(event.dataTransfer)) return
                  event.preventDefault()
                  if (!uploadsEnabled || uploading) return
                  fileDragDepth.current += 1
                  setDraggingFile(true)
                }}
                onDragOver={(event) => {
                  if (!dataTransferHasFiles(event.dataTransfer)) return
                  event.preventDefault()
                  event.dataTransfer.dropEffect = uploadsEnabled && !uploading ? 'copy' : 'none'
                }}
                onDragLeave={(event) => {
                  if (!dataTransferHasFiles(event.dataTransfer) || !draggingFile) return
                  event.preventDefault()
                  fileDragDepth.current = Math.max(0, fileDragDepth.current - 1)
                  if (fileDragDepth.current === 0) setDraggingFile(false)
                }}
                onDrop={(event) => {
                  if (!dataTransferHasFiles(event.dataTransfer)) return
                  event.preventDefault()
                  resetFileDrag()
                  if (!uploadsEnabled || uploading) return
                  if (event.dataTransfer.files.length !== 1) {
                    const firstFile = event.dataTransfer.files[0]
                    if (firstFile === undefined) return
                    setUploadFeedback({
                      state: 'error',
                      file: firstFile,
                      message: 'Drop one file at a time.',
                      retryable: false
                    })
                    return
                  }
                  const file = event.dataTransfer.files[0]
                  if (file !== undefined) void uploadFile(file)
                }}
                onSubmit={(event) => {
                  event.preventDefault()
                  if (canSubmit) void send()
                }}
              >
                {draggingFile && (
                  <div
                    className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] border-2 border-dashed border-primary/60 bg-background/95 px-4 text-center"
                    role="status"
                    aria-live="polite"
                  >
                    <div className="flex items-center gap-2 text-sm font-medium text-primary">
                      <span className="rounded-full bg-primary/10 p-2" aria-hidden="true">
                        <Upload className="size-5" />
                      </span>
                      Drop file to add it to Content
                    </div>
                  </div>
                )}
                {(tags.length > 0 || documents.length > 0) && (
                  <div className="flex flex-wrap gap-1.5 px-3 pt-3">
                    {tags.map((tag) => (
                      <span
                        key={`tag:${tag.id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-accent-strong px-2 py-1 text-xs text-accent-foreground"
                      >
                        {tag.label}
                        <button
                          type="button"
                          aria-label={`Remove ${tag.label}`}
                          onClick={() => {
                            invalidateAmbiguousCommand()
                            setTags((current) => current.filter((item) => item.id !== tag.id))
                          }}
                        >
                          <X className="size-3" />
                        </button>
                      </span>
                    ))}
                    {documents.map((document) => (
                      <span
                        key={`document:${document.id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs"
                      >
                        {document.label}
                        <button
                          type="button"
                          aria-label={`Remove ${document.label}`}
                          onClick={() => {
                            invalidateAmbiguousCommand()
                            setDocuments((current) => current.filter((item) => item.id !== document.id))
                          }}
                        >
                          <X className="size-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <Label htmlFor="conversation-composer" className="sr-only">
                  Ask Snowvault
                </Label>

                <Textarea
                  ref={textareaRef}
                  id="conversation-composer"
                  aria-label="Ask Snowvault"
                  {...(narrowingEnabled
                    ? {
                        role: 'combobox',
                        'aria-autocomplete': 'list' as const,
                        'aria-expanded': suggestionsOpen,
                        'aria-controls': suggestionListID,
                        ...(highlightedSuggestionID ? { 'aria-activedescendant': highlightedSuggestionID } : {})
                      }
                    : {})}
                  value={content}
                  disabled={submitting}
                  placeholder={`Describe the work in ${projectName}`}
                  rows={1}
                  className="max-h-36 min-h-14 resize-none border-0 bg-transparent px-4 pt-3 pb-2 text-[0.95rem] shadow-none focus-visible:ring-0 dark:bg-transparent"
                  onChange={(event) => {
                    retryCommand.current = undefined
                    setContent(event.currentTarget.value)
                    setError(undefined)
                    resetNarrowing()
                  }}
                  onPaste={(event) => {
                    const file = event.clipboardData.files[0]
                    if (file === undefined || !uploadsEnabled || uploading) return
                    event.preventDefault()
                    void uploadFile(file)
                  }}
                  onKeyDown={(event) => {
                    if (suggestionsOpen) {
                      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault()
                        const step = event.key === 'ArrowDown' ? 1 : -1
                        setHighlightedSuggestion(
                          // At rest the highlight sits below the list. ArrowDown enters at the first
                          // entry and ArrowUp at the last; from there both directions wrap.
                          activeSuggestion < 0
                            ? step === 1
                              ? 0
                              : matchedSuggestions.length - 1
                            : (activeSuggestion + step + matchedSuggestions.length) % matchedSuggestions.length
                        )
                        return
                      }
                      if (event.key === 'Escape') {
                        setSuggestionsDismissed(true)
                        setHighlightedSuggestion(-1)
                        return
                      }
                      const highlighted = matchedSuggestions[activeSuggestion]
                      if (
                        event.key === 'Enter' &&
                        !event.shiftKey &&
                        !event.nativeEvent.isComposing &&
                        highlighted !== undefined
                      ) {
                        // A highlighted phrase fills the composer and nothing more. Narrowing offers
                        // wording, it never sends on the user's behalf.
                        event.preventDefault()
                        fillFromSuggestion(highlighted)
                        return
                      }
                    }
                    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && canSubmit) {
                      event.preventDefault()
                      void send()
                    }
                  }}
                />
                <div className="flex flex-wrap items-center gap-1 px-2 pb-2">
                  <PopoverTrigger asChild>
                    <Button type="button" size="sm" variant="ghost">
                      <Plus className="size-4" aria-hidden="true" />
                      Context
                    </Button>
                  </PopoverTrigger>
                  <Button
                    type="submit"
                    size="icon"
                    className="ml-auto size-8 rounded-full"
                    disabled={!canSubmit}
                    aria-label="Send message"
                  >
                    <ArrowUp className="size-4" />
                  </Button>
                </div>
              </form>
            </PopoverAnchor>
            <PopoverContent
              aria-label="Add context"
              side="top"
              align="start"
              sideOffset={8}
              collisionPadding={8}
              avoidCollisions={false}
              className="max-h-[min(28rem,var(--radix-popover-content-available-height))] w-(--radix-popover-trigger-width) [scrollbar-color:var(--muted-foreground)_transparent] overflow-y-auto overscroll-contain rounded-2xl p-2"
            >
              {contextView === 'home' ? (
                <>
                  <p className="px-3 pt-1 pb-2 text-xs font-medium text-muted-foreground">Add context</p>
                  {attachmentsEnabled && (
                    <>
                      <Button
                        ref={contextHomeAction}
                        type="button"
                        variant="ghost"
                        className="min-h-11 w-full justify-start rounded-lg px-3"
                        onClick={() => setContextView('documents')}
                      >
                        <FilePlus2 className="size-4 text-muted-foreground" aria-hidden="true" />
                        Attach documents
                        <ChevronRight className="ml-auto size-4 text-muted-foreground" aria-hidden="true" />
                      </Button>
                      {uploadsEnabled && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="min-h-11 w-full justify-start rounded-lg px-3"
                          disabled={submitting || uploading || selectedDocumentIDs.length >= maxConversationAttachments}
                          onClick={() => {
                            setContextOpen(false)
                            fileInputRef.current?.click()
                          }}
                        >
                          <Upload className="size-4 text-muted-foreground" aria-hidden="true" />
                          Upload a file
                        </Button>
                      )}
                    </>
                  )}
                  <Button
                    ref={attachmentsEnabled ? undefined : contextHomeAction}
                    type="button"
                    variant="ghost"
                    className="min-h-11 w-full justify-start rounded-lg px-3"
                    onClick={() => setContextView('tags')}
                  >
                    <Tag className="size-4 text-muted-foreground" aria-hidden="true" />
                    Add tags
                    <ChevronRight className="ml-auto size-4 text-muted-foreground" aria-hidden="true" />
                  </Button>
                  {globalContentEnabled && (
                    <div className="mt-2 flex min-h-11 items-center gap-3 border-t px-3 pt-2">
                      <Checkbox
                        id="conversation-org-content"
                        checked={includeOrgContent}
                        onCheckedChange={(value) => {
                          retryCommand.current = undefined
                          setIncludeOrgContent(value === true)
                          setError(undefined)
                        }}
                      />
                      <Label htmlFor="conversation-org-content" className="text-sm font-normal">
                        Include organisation content
                      </Label>
                    </div>
                  )}
                  <p className="mx-3 mt-2 border-t pt-2 pb-1 text-xs leading-5 text-muted-foreground">
                    Snowvault cites what it reads. Spreadsheet figures are computed outside the model.
                  </p>
                </>
              ) : (
                <>
                  <div className="mb-2 flex items-center gap-2 px-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-9 shrink-0 rounded-lg"
                      aria-label="Back to context"
                      onClick={() => setContextView('home')}
                    >
                      <ArrowLeft className="size-4" aria-hidden="true" />
                    </Button>
                    <h3 className="text-sm font-medium">
                      {contextView === 'documents' ? 'Attach documents' : 'Add tags'}
                    </h3>
                  </div>
                  {contextView === 'documents' ? (
                    <OptionPicker
                      key="documents"
                      label="Attach documents"
                      action="Attach"
                      icon="document"
                      load={loadDocuments}
                      selected={selectedDocumentIDs}
                      maximum={maxConversationAttachments}
                      onAdd={(document) => {
                        invalidateAmbiguousCommand()
                        setDocuments((current) => [...current, document])
                      }}
                    />
                  ) : (
                    <OptionPicker
                      key="tags"
                      label="Add tags"
                      action="Add"
                      icon="tag"
                      load={loadTags}
                      {...(createTag === undefined ? {} : { onCreate: createTag })}
                      selected={selectedTagIDs}
                      maximum={maxConversationTags}
                      onAdd={(tag) => {
                        invalidateAmbiguousCommand()
                        setTags((current) =>
                          current.length >= maxConversationTags || current.some((item) => item.id === tag.id)
                            ? current
                            : [...current, tag]
                        )
                      }}
                    />
                  )}
                </>
              )}
            </PopoverContent>
          </Popover>
          {uploadFeedback?.state === 'uploading' && (
            <div
              className="mt-2 flex items-center gap-2 text-sm text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
              <span className="min-w-0 truncate">Adding {uploadFeedback.file.name} to Content...</span>
            </div>
          )}
          {uploadFeedback?.state === 'success' && (
            <p className="mt-2 text-sm text-muted-foreground" role="status" aria-live="polite">
              {uploadFeedback.message}
            </p>
          )}
          {uploadFeedback?.state === 'error' && (
            <div className="mt-2 flex items-center justify-between gap-3 text-sm text-destructive" role="alert">
              <span>{uploadFeedback.message}</span>
              {uploadFeedback.retryable && (
                <Button type="button" variant="ghost" size="sm" onClick={() => void uploadFile(uploadFeedback.file)}>
                  <RotateCw className="size-4" />
                  Try uploading again
                </Button>
              )}
            </div>
          )}
          {error && (
            <div className="mt-2 flex items-center justify-between gap-3 text-sm text-destructive" role="alert">
              <span>{error}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => retryCommand.current && void send(retryCommand.current.command)}
              >
                <RotateCw className="size-4" />
                Try sending again
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
